import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import CalendarFilter from './CalendarFilter';
import useUsers from '../../context/UsersContext';
import masterDataService from '../../service/masterData.service';
import colorFinder from '../../utils/colorFinder';
import useConfig from '../../context/ConfigContext';
import taskCalendarService from '../../service/taskCalendar.service';
import SimpleTaskEventContent from './SimpleTaskEventContent';
import simpletaskService from '../../service/simpletask.service';
import SimpleTaskDialog from './SimpleTaskDialog';
import { useMediaQuery } from '@mui/material';
import EmployeeAttendanceDialog from '../EmployeeAttendance/EmployeeAttendanceDialog';
import useParam from '../../context/ParamContext';
import { generateDefaultTime } from '../../utils/helper';

export const TaskCalendar = () => {
  const { user, getUser, getRole } = useUsers();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const calendarRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const SWIPE_THRESHOLD = 80;
  const [slideClass, setSlideClass] = useState('');
  const [events, setEvents] = useState([]);
  const [dateRange, setDateRange] = useState({});
  const [query, setQuery] = useState({
    filter: { filterModel: { items: [], logicOperator: 'and' } },
  });
  const [resources, setResources] = useState({
    user: [],
    employee: [],
  });
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  });
  const [simpleTaskDialog, setSimpleTaskDialog] = useState({ open: false, data: {} });
  const [taskChanged, setTaskChanged] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [roles, setRoles] = useState({});
  const defSimpleTaskReportDialog = {
    open: false,
    data: {},
  };
  const [showSimpleTaskReportDialog, setShowSimpleTaskReportDialog] = useState(defSimpleTaskReportDialog);
  const [isLoading, setIsLoading] = useState(true);

  const getFilterItems = () => query?.filter?.filterModel?.items ?? [];

  const isLgUp = useMediaQuery('(min-width:1060px)');

  const hasResourceFilter = useMemo(() => {
    const items = getFilterItems();

    return items.some((item) => {
      const isResourceField = item.field === 'responsiblePersonId' || item.field === 'employeeId';

      const values = Array.isArray(item.value) ? item.value : [];
      return isResourceField && values.length > 0;
    });
  }, [query]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
    getConfig();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole('TASK_CALENDAR_SEE_ONLY_OWN');

      setRoles(roles);
    }
  }, [user]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setHeaderOffset(mq.matches ? 64 : 0);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();

      if (calendarApi.view.type !== 'dayGridDay') {
        calendarApi.changeView('dayGridDay', date.startDate.toDate());
      } else {
        calendarApi.gotoDate(date.startDate.toDate());
      }
    }
  }, [date.startDate, date.endDate]);

  useEffect(() => {
    if (date.startDate !== null && date.endDate !== null) {
      setDateRange(date);
    }
  }, [date.startDate, date.endDate]);

  const filterItemsKey = JSON.stringify(query?.filter?.filterModel?.items ?? []);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate && hasResourceFilter) {
      getAllCalendarDays();
    } else {
      setEvents([]);
    }
  }, [dateRange.startDate, dateRange.endDate, hasResourceFilter, filterItemsKey]);

  useEffect(() => {
    if (taskChanged && hasResourceFilter) getAllCalendarDays();
  }, [taskChanged]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const resources = {
          user: resp[0],
          employee: resp[1],
        };

        const filteredEmployee = resources.employee.filter((employee) => employee.active);

        setResources({ ...resources, employee: filteredEmployee });
        setIsLoading(false);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  const calendarViews = {
    dayGridMonth: {
      dayHeaderFormat: { weekday: 'short' },
      dayMaxEventRows: true,
    },
    dayGridWeek: {
      dayHeaderFormat: { month: 'short', day: 'numeric', weekday: 'short' },
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    dayGridDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
  };

  const getAllCalendarDays = () => {
    const reqData = {
      query,
      dateRange,
    };
    taskCalendarService.getCalendarDays(reqData).then((data) => {
      const coloredData = data.map((event) => {
        const status = event?.status?.color;
        const color = colorFinder(status);
        return {
          ...event,
          backgroundColor: color.colorBgCode,
        };
      });
      setEvents(coloredData);
      setTaskChanged(false);
    });
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (dateRange.startDate?.isSame(startDate, 'day') && dateRange.endDate?.isSame(endDate, 'day')) {
      return;
    }
    setDateRange({ startDate, endDate });
  };

  const getSimpleTaskById = (id) => {
    setSimpleTaskDialog((prev) => ({
      ...prev,
      open: true,
    }));

    simpletaskService.getTaskById(id).then((data) => {
      setSimpleTaskDialog((prev) => ({
        ...prev,
        data: data,
      }));
    });
  };

  const handleSelectDate = (selectInfo) => {
    if (roles.TASK_CALENDAR_SEE_ONLY_OWN) return;
    const { start, allDay } = selectInfo;

    let startDate = dayjs(start);

    if (allDay) {
      startDate = config.defTaskFromTime
        ? generateDefaultTime(startDate, config.defTaskFromTime)
        : startDate.startOf('day');
    }

    const duration = config?.defTaskDuration ?? 1;

    const data = {
      startDate,
      endDate: dayjs(startDate).add(duration, 'hour'),
      duration,
    };

    setSimpleTaskDialog({
      open: true,
      data: data,
    });
  };

  const handleTouchStart = (event) => {
    if (isLgUp) return;

    if (event.target.closest('.fc-header-toolbar')) {
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }

    const touch = event.touches[0];

    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const animateCalendarChange = (direction) => {
    const calendarApi = calendarRef.current?.getApi();

    const exitClass = direction === 'next' ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0';
    const enterClass = direction === 'next' ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0';

    setSlideClass(exitClass);

    setTimeout(() => {
      if (direction === 'next') {
        calendarApi?.next();
      } else {
        calendarApi?.prev();
      }

      setSlideClass(`transition-none ${enterClass}`);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSlideClass('translate-x-0 opacity-100');
        });
      });
    }, 200);
  };

  const handleTouchEnd = (event) => {
    if (isLgUp || touchStartX.current === null || touchStartY.current === null) {
      return;
    }

    const touch = event.changedTouches[0];

    const diffX = touch.clientX - touchStartX.current;
    const diffY = touch.clientY - touchStartY.current;

    touchStartX.current = null;
    touchStartY.current = null;

    const isHorizontalSwipe = Math.abs(diffX) >= SWIPE_THRESHOLD && Math.abs(diffX) > Math.abs(diffY) * 1.5;

    if (!isHorizontalSwipe) return;

    animateCalendarChange(diffX < 0 ? 'next' : 'prev');
  };

  return (
    <div className={`taskListCalendar calendarView flex ${isLgUp ? `flex-row` : 'flex-col'} bg-[#f3f4f6]`}>
      <div
        className={`overflow-y-scroll overflow-x-hidden custom-scrollbar ${isLgUp ? 'w-[380px]' : 'max-h-[250px]'}`}
        style={{ height: isLgUp ? `calc(100dvh - ${headerOffset + 20}px)` : 'auto' }}
      >
        <CalendarFilter
          dates={{ date, setDate }}
          resources={resources}
          queryState={{ query, setQuery }}
          hasResourceFilter={hasResourceFilter}
          roles={roles}
          isLgUp={isLgUp}
          isLoading={isLoading}
        />
      </div>
      <div className="w-full overflow-hidden bg-white">
        <div
          className={`w-full transition-all duration-200 ease-out ${slideClass}`}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{
            touchAction: 'pan-y',
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={isLgUp ? 'dayGridWeek' : 'dayGridDay'}
            headerToolbar={{
              left: isLgUp ? 'prev,next,today' : 'today',
              center: 'title',
              right: 'dayGridDay,dayGridWeek,dayGridMonth',
            }}
            locales={[huLocale]}
            views={calendarViews}
            datesSet={handleDatesSet}
            nowIndicator={true}
            events={events}
            selectable={true}
            select={handleSelectDate}
            eventClick={(eventInfo) => {
              const { event } = eventInfo;
              const id = parseInt(event.id.substring(1));

              getSimpleTaskById(id);
            }}
            timeZone="UTC"
            eventContent={(eventInfo) => {
              return (
                <SimpleTaskEventContent
                  eventInfo={eventInfo}
                  resources={resources}
                  setShowSimpleTaskReportDialog={setShowSimpleTaskReportDialog}
                />
              );
            }}
          />
        </div>
      </div>

      {showSimpleTaskReportDialog.open && (
        <EmployeeAttendanceDialog
          dialogData={showSimpleTaskReportDialog}
          onClose={() => setShowSimpleTaskReportDialog(defSimpleTaskReportDialog)}
        />
      )}

      <SimpleTaskDialog
        open={simpleTaskDialog.open}
        handleClose={() =>
          setSimpleTaskDialog({
            open: false,
            data: {},
          })
        }
        data={simpleTaskDialog.data}
        changed={{ setTaskChanged }}
      />
    </div>
  );
};
